<template>
	<div>
		<loader v-if="!formReady"/>
		<template v-else>
			<header class="flex-apart">
				<div>
					<h1 class="alpha heavy">Report</h1>
					<div class="report__period">
						<h2 v-if="report" class="beta"><span v-html="$present('date', report.start)"/>—<span
							v-html="$present('date', report.end)"/></h2>
					</div>
				</div>
			</header>
			<div>
				<div class="sections">
					<div class="section--floating section--full">
						<form @submit.prevent>
							<label v-if="$authed().isStaff"><span>Service Provider </span>
								<v-typeahead
									:datasets="form.options.typeahead"
									v-on:autocompleted="(data) => form.set('service_provider', data.identify)"/>
							</label>
							<label><span>Period </span>
								<select v-model="form.data.period">
									<option
										v-for="(period, periodValue) in form.options.period" :value="periodValue"
										v-html="period"/>
								</select>
							</label>
							<template v-if="form.data.period === 'custom'">
								<label class="filters__filter"><span>From </span>
									<v-datepicker
										v-model="form.data.from"
										:max-date="form.data.to ? new Date(form.data.to) : new Date()"
										date-display-format="DD/MM/YYYY"
										v-on:change="generateReport()"/>
								</label>
								<label class="filters__filter"><span>To </span>
									<v-datepicker
										v-model="form.data.to"
										:max-date="new Date()"
										:min-date="form.data.from ? new Date(form.data.from) : null"
										date-display-format="DD/MM/YYYY"
										v-on:changed="generateReport()"/>
								</label>
							</template>
						</form>
					</div>
					<loader v-if="generatingReport" class="loader--bordered section--full" text="Generating Report"/>
					<v-tabs v-if="report" class="section--full tabs--switch tabs--switch--shadow">
						<v-tab id="stats" class="sections" label="Stats">
							<section class="section--floating section--full">
								<header class="flex-apart">
									<h2 class="beta">Introductions</h2>
									<div class="flex-apart">
										<dl class="flex-apart">
											<div class="metrics__metric--number metrics__metric--right">
												<dt class="metrics__metric__stats__title">Total</dt>
												<dd
													class="metrics__metric__stats__value --large"
													v-html="report.data.introductions.total"/>
											</div>
											<progress-indicator
												:percentage="report.data.introductions.conversionRate.percent"
												:titleSuffix="false"
												class="metrics__metric--progress--small metrics__metric--right"
												titlePrefix="Conversion Rate"/>
										</dl>
										<button
											class="button button--primary--small"
											@click="download('introductions')">Download
										</button>
									</div>
								</header>
								<div class="section--flat flex-even">
									<section class="metrics">
										<header>
											<h2 class="delta heavy center">Action Required</h2>
										</header>
										<dl class="flex-apart">
											<div class="metrics__metric--number">
												<dt class="metrics__metric__stats__title">Opt-in</dt>
												<dd
													class="metrics__metric__stats__value --large"
													v-html="report.data.introductions.optInRequired.total"/>
												<progress-indicator
													:percentage="report.data.introductions.optInRequired.percent.percent"
													:titleSuffix="false"
													class="metrics__metric--progress--no-title metrics__metric--progress--small"
													tag="dd"/>
											</div>
											<div class="metrics__metric--number">
												<dt class="metrics__metric__stats__title">Instruct</dt>
												<dd
													class="metrics__metric__stats__value --large"
													v-html="report.data.introductions.comparisonInstructionRequired.total"/>
												<progress-indicator
													:percentage="report.data.introductions.comparisonInstructionRequired.percent.percent"
													:titleSuffix="false"
													class="metrics__metric--progress--no-title metrics__metric--progress--small"
													tag="dd"/>
											</div>
										</dl>
									</section>
									<section class="metrics">
										<header>
											<h2 class="delta heavy center">Active</h2>
										</header>
										<dl class="flex-apart">
											<div class="metrics__metric--number">
												<dt class="metrics__metric__stats__title">Leads</dt>
												<dd
													class="metrics__metric__stats__value --large"
													v-html="report.data.introductions.activeLeads.total"/>
												<progress-indicator
													:percentage="report.data.introductions.activeLeads.percent.percent"
													:titleSuffix="false"
													class="metrics__metric--progress--no-title metrics__metric--progress--small"
													tag="dd"/>
											</div>
											<div class="metrics__metric--number">
												<dt class="metrics__metric__stats__title">Instructions</dt>
												<dd
													class="metrics__metric__stats__value --large"
													v-html="report.data.introductions.activeInstructions.total"/>
												<progress-indicator
													:percentage="report.data.introductions.activeInstructions.percent.percent"
													:titleSuffix="false"
													class="metrics__metric--progress--no-title metrics__metric--progress--small"
													tag="dd"/>
											</div>
										</dl>
									</section>
									<section class="metrics">
										<header>
											<h2 class="delta heavy center">Concluded</h2>
										</header>
										<dl class="flex-apart">
											<div class="metrics__metric--number">
												<dt class="metrics__metric__stats__title">Closed</dt>
												<dd
													class="metrics__metric__stats__value --large"
													v-html="report.data.introductions.closed.total"/>
												<progress-indicator
													:percentage="report.data.introductions.closed.percent.percent"
													:titleSuffix="false"
													class="metrics__metric--progress--no-title metrics__metric--progress--small"
													tag="dd"/>
											</div>
											<div class="metrics__metric--number">
												<dt class="metrics__metric__stats__title">Complete</dt>
												<dd
													class="metrics__metric__stats__value --large"
													v-html="report.data.introductions.complete.total"/>
												<progress-indicator
													:percentage="report.data.introductions.complete.percent.percent"
													:titleSuffix="false"
													class="metrics__metric--progress--no-title metrics__metric--progress--small"
													tag="dd"/>
											</div>
										</dl>
									</section>
								</div>
							</section>
							<section class="section--floating section--full">
								<header class="flex-apart">
									<h2 class="beta">ID Checks</h2>
									<div class="flex-apart">
										<dl class="flex-apart">
											<div class="metrics__metric--number metrics__metric--right">
												<dt class="metrics__metric__stats__title">Total</dt>
												<dd
													class="metrics__metric__stats__value --large"
													v-html="report.data.idChecks.total"/>
											</div>
											<div class="metrics__metric--number metrics__metric--right">
												<dt class="metrics__metric__stats__title">Average Completion Time</dt>
												<dd
													v-if="!report.data.idChecks.averageCompletionTime"
													class="metrics__metric__stats__value --large">—
												</dd>
												<dd
													v-else
													class="metrics__metric__stats__value --large"
													v-html="report.data.idChecks.averageCompletionTime"/>
											</div>
										</dl>
										<button
											class="button button--primary--small" @click="download('idChecks')">Download
										</button>
									</div>
								</header>
								<div class="section--flat flex-even">
									<section class="metrics">
										<header>
											<h2 class="delta heavy center">Action Required</h2>
										</header>
										<dl class="flex-apart">
											<div class="metrics__metric--number">
												<dt class="metrics__metric__stats__title">Registration</dt>
												<dd
													class="metrics__metric__stats__value --large"
													v-html="report.data.idChecks.registrationRequired.total"/>
												<progress-indicator
													:percentage="report.data.idChecks.registrationRequired.percent.percent"
													:titleSuffix="false"
													class="metrics__metric--progress--no-title metrics__metric--progress--small"
													tag="dd"/>
											</div>
											<div class="metrics__metric--number">
												<dt class="metrics__metric__stats__title">Payment</dt>
												<dd
													class="metrics__metric__stats__value --large"
													v-html="report.data.idChecks.paymentRequired.total"/>
												<progress-indicator
													:percentage="report.data.idChecks.registrationRequired.percent.percent"
													:titleSuffix="false"
													class="metrics__metric--progress--no-title metrics__metric--progress--small"
													tag="dd"/>
											</div>
										</dl>
									</section>
									<section class="metrics">
										<header>
											<h2 class="delta heavy center">Progressing</h2>
										</header>
										<dl>
											<div class="metrics__metric--number">
												<dt class="metrics__metric__stats__title">Ordered</dt>
												<dd
													class="metrics__metric__stats__value --large"
													v-html="report.data.idChecks.ordered.total"/>
												<progress-indicator
													:percentage="report.data.idChecks.ordered.percent.percent"
													:titleSuffix="false"
													class="metrics__metric--progress--no-title metrics__metric--progress--small"
													tag="dd"/>
											</div>
										</dl>
									</section>
									<section class="metrics">
										<header>
											<h2 class="delta heavy center">Concluded</h2>
										</header>
										<dl>
											<div class="metrics__metric--number">
												<dt class="metrics__metric__stats__title">Complete</dt>
												<dd
													class="metrics__metric__stats__value --large"
													v-html="report.data.idChecks.complete.total"/>
												<progress-indicator
													:percentage="report.data.idChecks.complete.percent.percent"
													:titleSuffix="false"
													class="metrics__metric--progress--no-title metrics__metric--progress--small"
													tag="dd"/>
											</div>
										</dl>
									</section>
								</div>
							</section>
							<section class="section--floating section--full">
								<header class="flex-apart">
									<h2 class="beta">Search Packs</h2>
									<div class="flex-apart">
										<dl class="flex-apart">
											<div class="metrics__metric--number metrics__metric--right">
												<dt class="metrics__metric__stats__title">Total</dt>
												<dd
													class="metrics__metric__stats__value --large"
													v-html="report.data.searchPacks.total"/>
											</div>
											<div class="metrics__metric--number metrics__metric--right">
												<dt class="metrics__metric__stats__title">Average Completion Time</dt>
												<dd
													v-if="!report.data.searchPacks.averageCompletionTime"
													class="metrics__metric__stats__value --large">—
												</dd>
												<dd
													v-else
													class="metrics__metric__stats__value --large"
													v-html="report.data.searchPacks.averageCompletionTime"/>
											</div>
										</dl>
										<button
											class="button button--primary--small"
											@click="download('searchPacks')">Download
										</button>
									</div>
								</header>
								<div class="section--flat flex-even">
									<section class="metrics">
										<header>
											<h2 class="delta heavy center">Action Required</h2>
										</header>
										<dl class="flex-apart">
											<div class="metrics__metric--number">
												<dt class="metrics__metric__stats__title">Registration</dt>
												<dd
													class="metrics__metric__stats__value --large"
													v-html="report.data.searchPacks.registrationRequired.total"/>
												<progress-indicator
													:percentage="report.data.searchPacks.registrationRequired.percent.percent"
													:titleSuffix="false"
													class="metrics__metric--progress--no-title metrics__metric--progress--small"
													tag="dd"/>
											</div>
											<div class="metrics__metric--number">
												<dt class="metrics__metric__stats__title">Payment</dt>
												<dd
													class="metrics__metric__stats__value --large"
													v-html="report.data.searchPacks.paymentRequired.total"/>
												<progress-indicator
													:percentage="report.data.searchPacks.paymentRequired.percent.percent"
													:titleSuffix="false"
													class="metrics__metric--progress--no-title metrics__metric--progress--small"
													tag="dd"/>
											</div>
											<div class="metrics__metric--number">
												<dt class="metrics__metric__stats__title">Select Pack</dt>
												<dd
													class="metrics__metric__stats__value --large"
													v-html="report.data.searchPacks.selectPackRequired.total"/>
												<progress-indicator
													:percentage="report.data.searchPacks.selectPackRequired.percent.percent"
													:titleSuffix="false"
													class="metrics__metric--progress--no-title metrics__metric--progress--small"
													tag="dd"/>
											</div>
										</dl>
									</section>
									<section class="metrics">
										<header>
											<h2 class="delta heavy center">Progressing</h2>
										</header>
										<dl>
											<div class="metrics__metric--number">
												<dt class="metrics__metric__stats__title">Ordered</dt>
												<dd
													class="metrics__metric__stats__value --large"
													v-html="report.data.searchPacks.ordered.total"/>
												<progress-indicator
													:percentage="report.data.searchPacks.ordered.percent.percent"
													:titleSuffix="false"
													class="metrics__metric--progress--no-title metrics__metric--progress--small"
													tag="dd"/>
											</div>
										</dl>
									</section>
									<section class="metrics">
										<header>
											<h2 class="delta heavy center">Concluded</h2>
										</header>
										<dl>
											<div class="metrics__metric--number">
												<dt class="metrics__metric__stats__title">Complete</dt>
												<dd
													class="metrics__metric__stats__value --large"
													v-html="report.data.searchPacks.complete.total"/>
												<progress-indicator
													:percentage="report.data.searchPacks.complete.percent.percent"
													:titleSuffix="false"
													class="metrics__metric--progress--no-title metrics__metric--progress--small"
													tag="dd"/>
											</div>
										</dl>
									</section>
								</div>
							</section>
						</v-tab>
						<v-tab id="commissions" label="Commissions">
							<section class="section--floating section--full">
								<header class="flex-apart">
									<h2 class="beta">Commissions</h2>
								</header>
								<dl class="sections">
									<div class="section--flat section--flexed sections">
										<div class="section--flexed metrics__metric__stats metrics__metric--number">
											<dt class="metrics__metric__stats__title">Owed</dt>
											<dd class="metrics__metric--number"><span
												class="metrics__metric__stats__value --large"
												v-html="$present('price', report.data.commissions.owed)"/> <span
												class="metrics__metric__stats__title">inc. VAT</span>
											</dd>
										</div>
									</div>
								</dl>
							</section>
						</v-tab>
					</v-tabs>
				</div>
			</div>
		</template>
	</div>
</template>
<script>

export default {
	data() {
		return {
			formReady: false,
			form: this.newForm({
				service_provider: "",
				period: "",
				from: "",
				to: "",
			}),
			generatingReport: false,
			report: null,
			poll: null,
		};
	},
	watch: {
		"form.data": {
			deep: true,
			handler() {
				console.log("watch handler");
				this.generateReport();
			},
		},
	},
	methods: {
		generateReport(silent = false) {
			if (
				this.$authed().isStaff &&
				!this.form.data.service_provider
			) {
				return;
			}

			clearTimeout(this.poll);

			this.report = silent ? this.report : null;
			this.generatingReport = silent ? false : true;

			this.form.post(this.route("report")).then(response => {
				this.generatingReport = false;
				this.report = response;
				this.poll = setTimeout(function () {
					this.generateReport(true);
				}.bind(this), 3000);
			});
		},
		download(report) {
			http().post(this.route("report/:report/download/csv", report), this.form.data, {
				responseType: "blob",
			}).then(response => {
				let fileName = response.headers["content-disposition"].split(";")[1].split("=")[1];
				let downloadTrigger = document.createElement("a");
				downloadTrigger.href = window.URL.createObjectURL(new Blob([response.data]));
				downloadTrigger.setAttribute("download", fileName);
				document.body.appendChild(downloadTrigger);
				downloadTrigger.click();
			});
		},
	},
	created() {
		http().get(this.route("form-data/report")).then(response => {
			this.form.setOptions(response.data);
			this.formReady = true;

			if (!this.$authed().isStaff) {
				this.generateReport();
			}
		});
	},

	destroyed() {
		clearTimeout(this.poll);
	},
};
</script>
